import React, { useCallback } from 'react';
import { useComparisonTimePeriodShortDisplayName, useMainTimePeriodShortDisplayName } from 'src/utils/Hooks';
import { useCategoryCount, useMainEntityName, useMainEntityType } from 'src/utils/Hooks/reduxSelectors';
import { DynamicSubtitle } from 'src/components/BeaconRedesignComponents/ExperimentalLayout/ContentV2/hooks/classes/DynamicSubtitle';
import { useTaxonomySubtitleText } from 'src/components/BeaconV2Components/common/BeaconProDynamicSubtitle/hooks';
import { KeywordFilter } from 'src/components/BeaconRedesignComponents/ExperimentalLayout/ContentV2/Summary/Visualizations/types';
import SubtitleFilters from 'src/components/Charts/SubtitleFilters';

/**
 * Instantiates a DynamicSubtitle object with the current entity, time period, and category count for building subtitles
 */
export const useDynamicSubtitle = () => {
  const entityType = useMainEntityType();
  const entityName = useMainEntityName();
  const mainTimePeriodName = useMainTimePeriodShortDisplayName();
  const comparisonTimePeriodName = useComparisonTimePeriodShortDisplayName();
  const categoryCount = useCategoryCount();

  const createSubtitle = useCallback(
    () =>
      new DynamicSubtitle({
        entityType,
        entityName,
        mainTimePeriodName,
        comparisonTimePeriodName,
        categoryCount
      }),
    [entityType, entityName, mainTimePeriodName, comparisonTimePeriodName, categoryCount]
  );

  return {
    createSubtitle
  };
};

/**
 * Hook that provides functions for generating subtitles for content charts
 */
export const useContentChartSubtitles = () => {
  const entityType = useMainEntityType();
  const { createSubtitle } = useDynamicSubtitle();
  const isCategoryOrSubcategory = entityType === 'category' || entityType === 'subCategory';

  const taxonomyText = useTaxonomySubtitleText();

  const generateSpiderChartSubtitle = useCallback(
    ({ formattedValue, metricName }: { formattedValue: string; metricName: string }) => {
      switch (entityType) {
        case 'product':
          return createSubtitle()
            .addText(
              ({ mainTimePeriodName, comparisonTimePeriodName }) =>
                `This product's ${metricName} is ${formattedValue}. The data only includes content during the last ${
                  mainTimePeriodName.toLowerCase().includes('year') ? 'year' : 'week'
                } period compared to the ${comparisonTimePeriodName}.`
            )
            .build();
        default:
          return createSubtitle()
            .addText(({ entityName }) =>
              isCategoryOrSubcategory ? `The ${entityName} ${entityType.toLocaleLowerCase()}` : entityName
            )
            .addText(metricName)
            .addText(taxonomyText)
            .addText('is')
            .addText(formattedValue)
            .addPunctuation('.')
            .addSpace()
            .addText(
              ({ mainTimePeriodName, comparisonTimePeriodName }) =>
                `The data only includes content during the last ${
                  mainTimePeriodName.toLowerCase().includes('year') ? 'year' : 'week'
                } period compared to the ${comparisonTimePeriodName}.`
            )
            .addText(<SubtitleFilters />)
            .build();
      }
    },
    [entityType, createSubtitle, taxonomyText, isCategoryOrSubcategory]
  );

  const generateSplineChartSubtitle = useCallback(
    ({
      formattedAverage,
      metricName,
      formattedPercentChange,
      direction
    }: {
      formattedAverage: string;
      metricName: string;
      formattedPercentChange: string;
      direction: 'higher' | 'lower';
    }) => {
      switch (entityType) {
        case 'product':
          return createSubtitle()
            .addText(
              ({ mainTimePeriodName, comparisonTimePeriodName }) =>
                `This product's ${metricName} is ${formattedAverage} during the ${mainTimePeriodName}, which is ${formattedPercentChange} ${direction} compared to the ${comparisonTimePeriodName}.`
            )
            .build();
        default:
          return createSubtitle()
            .addText(({ entityName }) =>
              isCategoryOrSubcategory ? `The ${entityName} ${entityType.toLocaleLowerCase()}` : entityName
            )
            .addText(metricName)
            .addText(taxonomyText)
            .addText('is')
            .addText(formattedAverage)
            .addText(
              ({ mainTimePeriodName, comparisonTimePeriodName }) =>
                `during the ${mainTimePeriodName} period, which is ${formattedPercentChange} ${direction} compared to the ${comparisonTimePeriodName}.`
            )
            .addText(<SubtitleFilters />)
            .build();
      }
    },
    [entityType, createSubtitle, taxonomyText, isCategoryOrSubcategory]
  );

  const generateBarChartSubtitle = useCallback(
    ({ metricName, groupByText }: { metricName: string; groupByText: string }) => {
      return createSubtitle()
        .addText(
          ({ entityName }) =>
            `The chart displays the ${metricName} for ${
              isCategoryOrSubcategory ? `the ${entityName} ${entityType.toLocaleLowerCase()}` : entityName
            }`
        )
        .addText(groupByText)
        .addText(
          ({ mainTimePeriodName, comparisonTimePeriodName }) =>
            `during the ${mainTimePeriodName} period compared to the ${comparisonTimePeriodName}.`
        )
        .build();
    },
    [createSubtitle, entityType, isCategoryOrSubcategory]
  );

  const generateTableGridSubtitle = useCallback(
    ({ view, metricName }: { view: 'table' | 'grid'; metricName: string }) => {
      return createSubtitle()
        .addText(
          ({ entityName, mainTimePeriodName, comparisonTimePeriodName }) =>
            `The ${view} displays the ${metricName} for ${
              isCategoryOrSubcategory ? `the ${entityName} ${entityType.toLocaleLowerCase()}` : entityName
            } by product for the ${mainTimePeriodName} period compared to the ${comparisonTimePeriodName}.`
        )
        .build();
    },
    [createSubtitle, entityType, isCategoryOrSubcategory]
  );

  const generateKeywordPerformanceSubtitle = useCallback(() => {
    switch (entityType) {
      case 'product':
        return createSubtitle()
          .addText(
            ({ mainTimePeriodName, comparisonTimePeriodName }) =>
              `This product's keyword performance is measured by three unique metrics of usage, productivity, and popularity. The data only includes content during the last ${
                mainTimePeriodName.toLowerCase().includes('year') ? 'year' : 'week'
              } period compared to the ${comparisonTimePeriodName}.`
          )
          .build();

      default:
        return createSubtitle()
          .addText(
            ({ entityName, mainTimePeriodName, comparisonTimePeriodName }) =>
              `${
                isCategoryOrSubcategory ? `The ${entityName} ${entityType.toLocaleLowerCase()}` : entityName
              } keyword performance is measured by three unique metrics of usage, productivity, and popularity. The data only includes content during the last ${
                mainTimePeriodName.toLowerCase().includes('year') ? 'year' : 'week'
              } period compared to the ${comparisonTimePeriodName}.`
          )

          .build();
    }
  }, [createSubtitle, entityType, isCategoryOrSubcategory]);

  const generateKeywordSubtitleForMetric = useCallback(
    ({ metric, formattedValue }: { metric: KeywordFilter; formattedValue: string }) => {
      switch (metric) {
        case KeywordFilter.Usage: {
          const text =
            entityType === 'product'
              ? `This product features ${formattedValue} keywords in the content of the product page.`
              : ({ entityName }) =>
                  `${
                    isCategoryOrSubcategory ? `The ${entityName} ${entityType.toLocaleLowerCase()}` : entityName
                  } products feature an average of ${formattedValue} keywords in the content of each product page.`;
          return createSubtitle().addText(text).build();
        }
        case KeywordFilter.Productivity: {
          const text =
            entityType === 'product'
              ? `This product has ${formattedValue} keywords that generate traffic to the product page.`
              : ({ entityName }) =>
                  `${
                    isCategoryOrSubcategory ? `The ${entityName} ${entityType.toLocaleLowerCase()}` : entityName
                  } products have on average ${formattedValue} keywords that generate organic traffic to the product page.`;
          return createSubtitle().addText(text).build();
        }
        case KeywordFilter.Popularity: {
          const text =
            entityType === 'product'
              ? `This product drives ${formattedValue} searches from the keywords used on the product page.`
              : ({ entityName }) =>
                  `${
                    isCategoryOrSubcategory ? `The ${entityName} ${entityType.toLocaleLowerCase()}` : entityName
                  } products drive on average ${formattedValue} organic searches from the keywords used on the product page.`;
          return createSubtitle().addText(text).build();
        }
        default:
          return '';
      }
    },
    [createSubtitle, entityType, isCategoryOrSubcategory]
  );

  return {
    generateSpiderChartSubtitle,
    generateSplineChartSubtitle,
    generateBarChartSubtitle,
    generateTableGridSubtitle,
    generateKeywordPerformanceSubtitle,
    generateKeywordSubtitleForMetric
  };
};
